import { ArchivedStatus } from 'components/domain/archived/ArchivedPicker/ArchivedPicker';
import { useQueryParams } from 'hooks';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import useTimeRangeTimeRangeTypeQueryParams from 'hooks/routing/useTimeRangeTimeRangeTypeQueryParams/useTimeRangeTimeRangeTypeQueryParams';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { TimesheetView } from 'store/timesheets/Timesheets';
import TimeRangeType from 'types/TimeRangeType';
import { Nullable } from 'types/util/Nullable';
import { QueryParam } from 'utils/constants/queryParam';
import { decodeUuidsFromSearchParam } from 'utils/uuidUtils';
import { TimesheetReportType } from '../utils/TimesheetReportType';
import { TimesheetsGraphType } from '../TimesheetsHeader/TimesheetsHeader';
import { DEFAULT_TIMESHEETS_ARCHIVED_STATUS } from '../utils/utils';

export interface ITimesheetsQueryPayload {
  timeRangeType: TimeRangeType;
  timesheetView: TimesheetView;
  startDate: Nullable<DateTime>;
  endDate: Nullable<DateTime>;
  memberGroupIds: string[];
  positionIds: string[];
  memberIds: string[];
  projectIds: string[];
  costCodeIds: string[];
  equipmentIds: string[];
  timesheetReportType: Nullable<TimesheetReportType>;
  archivedStatus: ArchivedStatus;
  timesheetGraphType: Nullable<TimesheetsGraphType>;
  costCodeGroupIds: string[];
  projectGroupIds: string[];
}

export default function useTimesheetsQueryParams(): ITimesheetsQueryPayload {
  const { getParam } = useQueryParams();
  const memberSettings = useMemberSettings();
  const parseTimeRangeTimeRangeType = useTimeRangeTimeRangeTypeQueryParams();

  return useMemo((): ITimesheetsQueryPayload => {
    const { timeRangeType, timeRange } = parseTimeRangeTimeRangeType(
      TimeRangeType.PAY_PERIOD,
      memberSettings?.web?.features?.timeCards?.timeRangeType,
      'utc'
    );

    return {
      timeRangeType: timeRangeType,
      timesheetView: getParam<TimesheetView>(QueryParam.TIMESHEET_VIEW) ?? TimesheetView.SUMMARY,
      startDate: timeRange?.startTime ?? null,
      endDate: timeRange?.endTime ?? null,
      memberGroupIds: decodeUuidsFromSearchParam(getParam(QueryParam.EMPLOYEE_GROUP_ID)),
      positionIds: decodeUuidsFromSearchParam(getParam(QueryParam.POSITION_ID)),
      memberIds: decodeUuidsFromSearchParam(getParam(QueryParam.EMPLOYEE_ID)),
      projectIds: decodeUuidsFromSearchParam(getParam(QueryParam.PROJECT_ID)),
      costCodeIds: decodeUuidsFromSearchParam(getParam(QueryParam.COST_CODE_ID)),
      equipmentIds: decodeUuidsFromSearchParam(getParam(QueryParam.EQUIPMENT_ID)),
      timesheetReportType: getParam<TimesheetReportType>(QueryParam.TIMESHEET_REPORT_TYPE) ?? null,
      archivedStatus: getParam(QueryParam.ARCHIVED_STATUS) ?? DEFAULT_TIMESHEETS_ARCHIVED_STATUS,
      timesheetGraphType: getParam<TimesheetsGraphType>(QueryParam.TIMESHEET_GRAPH_TYPE),
      costCodeGroupIds: decodeUuidsFromSearchParam(getParam(QueryParam.COST_CODE_GROUP_ID)),
      projectGroupIds: decodeUuidsFromSearchParam(getParam(QueryParam.PROJECT_GROUP_ID)),
    };
  }, [getParam, memberSettings?.web?.features?.timeCards?.timeRangeType, parseTimeRangeTimeRangeType]);
}
